import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import budgetService from '../../services/budgetService';
import ActiveAlertCard from './components/ActiveAlertCard';
import NotificationHistoryItem from './components/NotificationHistoryItem';
import AlertConfigPanel from './components/AlertConfigPanel';
import Icon from '../../components/AppIcon';
import UpgradeModal from '../../components/ui/UpgradeModal';
import Header from '../../components/ui/Header';

const BudgetAlertsNotifications = () => {
  const { user } = useAuth();
  const { canUse } = useSubscription();
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'history', 'config'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState({ feature: '', limit: 0, current: 0 });

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [alerts, notifs] = await Promise.all([
        budgetService?.getActiveAlerts(user?.id),
        budgetService?.getNotifications(user?.id)
      ]);
      setActiveAlerts(alerts);
      setNotifications(notifs);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await budgetService?.updateAlertStatus(alertId, 'dismissed');
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleSnoozeAlert = async (alertId) => {
    try {
      const snoozedUntil = new Date();
      snoozedUntil?.setHours(snoozedUntil?.getHours() + 24); // Snooze for 24 hours
      await budgetService?.updateAlertStatus(alertId, 'snoozed', snoozedUntil?.toISOString());
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await budgetService?.markNotificationRead(notificationId);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const getAlertPriorityColor = (alertType, percentageUsed) => {
    if (alertType === 'exceeded_limit' || percentageUsed >= 100) return 'destructive';
    if (alertType === 'approaching_limit' || percentageUsed >= 80) return 'warning';
    return 'primary';
  };

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
          {/* Header */}
          <div className="mb-24 md:mb-32">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-8">
              Budget Alerts & Notifications
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Monitor your spending alerts and notification history
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-destructive">{error}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="mb-24 md:mb-32">
            <div className="flex gap-8 border-b border-border">
              <button
                onClick={() => setActiveTab('alerts')}
                className={`px-20 py-12 font-medium transition-smooth min-h-[44px] relative ${
                  activeTab === 'alerts' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Active Alerts
                {activeAlerts?.length > 0 && (
                  <span className="ml-8 px-8 py-2 bg-destructive text-destructive-foreground text-xs rounded-full">
                    {activeAlerts?.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-20 py-12 font-medium transition-smooth min-h-[44px] relative ${
                  activeTab === 'history' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Notification History
                {unreadCount > 0 && (
                  <span className="ml-8 px-8 py-2 bg-primary text-primary-foreground text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('config')}
                className={`px-20 py-12 font-medium transition-smooth min-h-[44px] ${
                  activeTab === 'config' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Configuration
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'alerts' && (
            <div>
              {activeAlerts?.length === 0 ? (
                <div className="text-center py-40 bg-card rounded-lg border border-border">
                  <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-16" />
                  <h3 className="text-xl font-semibold text-foreground mb-8">No Active Alerts</h3>
                  <p className="text-muted-foreground">You are within your budget limits for all categories</p>
                </div>
              ) : (
                <div className="space-y-16">
                  {activeAlerts?.map((alert) => (
                    <ActiveAlertCard
                      key={alert?.id}
                      alert={alert}
                      onDismiss={() => handleDismissAlert(alert?.id)}
                      onSnooze={() => handleSnoozeAlert(alert?.id)}
                      priorityColor={getAlertPriorityColor(alert?.alertType, alert?.percentageUsed)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {notifications?.length === 0 ? (
                <div className="text-center py-40 bg-card rounded-lg border border-border">
                  <Icon name="Bell" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                  <h3 className="text-xl font-semibold text-foreground mb-8">No Notifications</h3>
                  <p className="text-muted-foreground">Your notification history will appear here</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {notifications?.map((notification) => (
                    <NotificationHistoryItem
                      key={notification?.id}
                      notification={notification}
                      onMarkAsRead={() => handleMarkAsRead(notification?.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'config' && (
            <AlertConfigPanel userId={user?.id} />
          )}

          {showUpgradeModal && (
            <UpgradeModal
              isOpen={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              feature={upgradeInfo?.feature}
              limit={upgradeInfo?.limit}
              current={upgradeInfo?.current}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BudgetAlertsNotifications;